import { useState, useEffect } from 'react'
import { useStore } from '../../store/useStore'
import { updateSymbols, fetchSchedulerStatus, updateSchedulerStatus } from '../../api/client'
import { ShieldAlert, Save } from 'lucide-react'

export default function SettingsUI() {
  const { symbols, loadSymbols } = useStore()

  const [password, setPassword] = useState('')
  const [cryptoText, setCryptoText] = useState('')
  const [stockText, setStockText] = useState('')
  const [fxText, setFxText] = useState('')
  const [schedulerEnabled, setSchedulerEnabled] = useState<boolean>(true)

  const [status, setStatus] = useState<{ type: 'idle' | 'success' | 'error'; message: string }>({
    type: 'idle',
    message: '',
  })
  const [isSaving, setIsSaving] = useState(false)

  // Initialize form when symbols load
  useEffect(() => {
    if (symbols) {
      setCryptoText(symbols.crypto.join(', '))
      setStockText(symbols.stock.join(', '))
      setFxText(symbols.fx.join(', '))
    }
  }, [symbols])

  // Load scheduler status on mount
  useEffect(() => {
    fetchSchedulerStatus()
      .then((res) => setSchedulerEnabled(res.enabled))
      .catch((err) => console.error('Failed to load scheduler status', err))
  }, [])

  const handleSave = async () => {
    if (!password) {
      setStatus({ type: 'error', message: '管理者パスワードを入力してください。' })
      return
    }

    try {
      setIsSaving(true)
      setStatus({ type: 'idle', message: '' })

      const newSymbols = {
        crypto: cryptoText.split(',').map((s) => s.trim()).filter(Boolean),
        stock: stockText.split(',').map((s) => s.trim()).filter(Boolean),
        fx: fxText.split(',').map((s) => s.trim()).filter(Boolean),
      }

      await updateSymbols(password, newSymbols)
      await updateSchedulerStatus(password, schedulerEnabled)
      
      setStatus({ type: 'success', message: '設定を保存しました。' })
      
      // Reload symbols into store
      await loadSymbols()
    } catch (err: any) {
      setStatus({ type: 'error', message: err.message || '設定の保存に失敗しました。' })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto p-6 text-trading-text">
      <div className="flex items-center gap-2 mb-6">
        <ShieldAlert className="w-6 h-6 text-trading-accent" />
        <h1 className="text-xl font-bold tracking-wider uppercase">システム設定</h1>
      </div>

      <div className="trading-card max-w-2xl flex flex-col gap-6">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-trading-muted mb-2">
            管理者パスワード
          </label>
          <input
            type="password"
            className="w-full bg-trading-bg/50 border border-trading-border rounded p-2 text-sm focus:outline-none focus:border-trading-accent"
            placeholder="管理者パスワード"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-trading-muted mb-2">
            バックグラウンド自動予測更新
          </label>
          <div className="flex items-center gap-3 bg-trading-bg/30 border border-trading-border/50 rounded-lg p-3">
            <button
              type="button"
              onClick={() => setSchedulerEnabled(!schedulerEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                schedulerEnabled ? 'bg-trading-green' : 'bg-trading-muted/40'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  schedulerEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <div className="flex flex-col">
              <span className="text-sm font-semibold">
                {schedulerEnabled ? '有効 (自動データ取得＆予測を実行中)' : '無効 (定時バックグラウンド処理を一時停止)'}
              </span>
              <span className="text-[10px] text-trading-muted">
                暗号資産(5分ごと)、株式・FX(15分ごと)のバックグラウンド定時推論の稼働状態を設定します。
              </span>
            </div>
          </div>
        </div>

        <div className="divider" />

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-trading-muted mb-2">
            暗号通貨 (Crypto)
          </label>
          <p className="text-[10px] text-trading-muted mb-2">カンマ区切りで入力してください (例: BTCUSDT, ETHUSDT)</p>
          <textarea
            className="w-full h-20 bg-trading-bg/50 border border-trading-border rounded p-2 text-sm font-mono focus:outline-none focus:border-trading-accent"
            value={cryptoText}
            onChange={(e) => setCryptoText(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-trading-muted mb-2">
            株式 (Stock)
          </label>
          <p className="text-[10px] text-trading-muted mb-2">カンマ区切りで入力してください (例: AAPL, TSLA)</p>
          <textarea
            className="w-full h-20 bg-trading-bg/50 border border-trading-border rounded p-2 text-sm font-mono focus:outline-none focus:border-trading-accent"
            value={stockText}
            onChange={(e) => setStockText(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-trading-muted mb-2">
            通貨ペア (FX)
          </label>
          <p className="text-[10px] text-trading-muted mb-2">カンマ区切りで入力してください (例: USDJPY=X, EURUSD=X)</p>
          <textarea
            className="w-full h-20 bg-trading-bg/50 border border-trading-border rounded p-2 text-sm font-mono focus:outline-none focus:border-trading-accent"
            value={fxText}
            onChange={(e) => setFxText(e.target.value)}
          />
        </div>

        {status.message && (
          <div
            className={`p-3 rounded text-sm font-semibold ${
              status.type === 'success'
                ? 'bg-trading-green/20 text-trading-green'
                : 'bg-trading-red/20 text-trading-red'
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="trading-button flex items-center gap-2 px-6 py-2"
          >
            <Save className="w-4 h-4" />
            {isSaving ? '保存中...' : '設定を保存'}
          </button>
        </div>
      </div>
    </div>
  )
}
